import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ExternalLink, RotateCcw, Undo2, History } from "lucide-react";
import { adminApi, parseError } from "./api";
import { Rich } from "@/components/site/Rich";
import { ImageInput } from "./ui";

const PATHS = { home: "/", about: "/about", water: "/water-mission", stories: "/research", events: "/events", repository: "/repository", contact: "/contact", footer: "/", nav: "/", notfound: "/this-page-does-not-exist" };
const sectionLabel = (s) => s.replace(/(\d+)/, " $1").replace(/^./, (c) => c.toUpperCase());
const MULTI = ["multiline", "markdown"];

const FieldActions = ({ f, draft, setDraft }) => (
  <div className="flex flex-wrap gap-3 text-xs font-bold">
    {draft !== f.value && <button type="button" onClick={() => setDraft(f.value)} className="inline-flex items-center gap-1 text-brand underline" data-testid={`content-undo-${f.key}`}><Undo2 className="h-3.5 w-3.5" aria-hidden="true" />Undo unsaved edit</button>}
    {f.previous != null && f.previous !== draft && <button type="button" onClick={() => setDraft(f.previous)} className="inline-flex items-center gap-1 text-brand underline" data-testid={`content-previous-${f.key}`}><History className="h-3.5 w-3.5" aria-hidden="true" />Restore previous version</button>}
    {f.default !== draft && <button type="button" onClick={() => setDraft(f.default)} className="inline-flex items-center gap-1 text-ink-muted underline" data-testid={`content-reset-${f.key}`}><RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />Reset to original copy</button>}
  </div>
);

const Field = ({ f, draft, setDraft, error }) => {
  const id = `cf-${f.key}`;
  const common = { id, value: draft, onChange: (e) => setDraft(e.target.value), className: `field ${error ? "border-[#B42318]" : ""}`, "aria-invalid": Boolean(error), "data-testid": `content-field-${f.key}` };
  const dirty = draft !== f.value;
  return (
    <div className="space-y-2 border-b border-line py-5 last:border-0" data-testid={`content-row-${f.key}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-sm font-bold text-ink">{f.label}{f.required && <span className="text-[#B42318]"> *</span>}</label>
        <span className="text-xs text-ink-muted">{dirty ? <span className="font-bold text-[#B54708]">Unsaved</span> : f.updatedBy && f.updatedBy !== "default" ? `Saved by ${f.updatedBy}` : "Original copy"}</span>
      </div>
      {f.type === "image" ? <ImageInput id={id} value={draft} onChange={setDraft} /> : MULTI.includes(f.type) ? <textarea rows={Math.min(8, Math.max(3, Math.ceil((draft || "").length / 90)))} {...common} /> : <input type={f.type === "order" ? "number" : "text"} min={f.type === "order" ? 1 : undefined} max={f.type === "order" ? 99 : undefined} {...common} />}
      {f.type === "markdown" && <p className="text-xs text-ink-muted">Line breaks are kept. Use **bold** and [link text](https://example.org) for formatting.</p>}
      {f.type === "markdown" && draft && (
        <div className="border-l-4 border-brand-green bg-mist px-4 py-3 text-sm leading-relaxed text-ink" data-testid={`content-preview-${f.key}`}>
          <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-ink-muted">Preview</p>
          <Rich text={draft} />
        </div>
      )}
      {error && <p role="alert" className="text-sm font-semibold text-[#B42318]" data-testid={`content-error-${f.key}`}>{error}</p>}
      <FieldActions f={f} draft={draft} setDraft={setDraft} />
    </div>
  );
};

export default function SiteContent() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [drafts, setDrafts] = useState({});
  const [errors, setErrors] = useState({});
  const [page, setPage] = useState("home");
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get("/site-content").then((r) => {
    setData(r.data);
    setDrafts(Object.fromEntries(r.data.fields.map((f) => [f.key, f.value])));
  }).catch((e) => setLoadError(parseError(e).message));
  useEffect(() => { load(); }, []);

  const dirtyKeys = useMemo(() => (data ? data.fields.filter((f) => drafts[f.key] !== f.value).map((f) => f.key) : []), [data, drafts]);
  useEffect(() => {
    if (!dirtyKeys.length) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirtyKeys.length]);

  if (loadError) return <p role="alert" className="text-[#B42318]">{loadError}</p>;
  if (!data) return <p role="status" className="text-ink-muted">Loading website content…</p>;

  const fields = data.fields.filter((f) => f.page === page);
  const sections = [...new Set(fields.map((f) => f.section))];
  const dirtyIn = (p) => data.fields.some((f) => f.page === p && dirtyKeys.includes(f.key));
  const setDraft = (k) => (v) => setDrafts((d) => ({ ...d, [k]: v }));

  const save = async () => {
    if (!dirtyKeys.length || saving) return;
    setSaving(true);
    try {
      const { data: res } = await adminApi.put("/site-content", { values: Object.fromEntries(dirtyKeys.map((k) => [k, drafts[k]])) });
      setErrors({});
      await load();
      toast.success(res.saved.length ? `Saved ${res.saved.length} change${res.saved.length === 1 ? "" : "s"}. The public website is updated.` : "No changes to save.");
    } catch (e) {
      const { message, fields: fe } = parseError(e);
      setErrors(fe);
      const first = data.fields.find((f) => fe[f.key]);
      if (first) {
        setPage(first.page);
        setTimeout(() => {
          const el = document.getElementById(`cf-${first.key}`);
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
          el?.focus({ preventScroll: true });
        }, 100);
      }
      toast.error(Object.keys(fe).length ? "Nothing was saved. Please fix the highlighted fields." : message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-site-content">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-brand">Website Content</h1>
          <p className="mt-2 max-w-2xl text-sm text-ink-muted">Edit the text shown on the public website. Changes go live as soon as you save. Stories, events and team profiles are managed in their own sections.</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {dirtyKeys.length > 0 && <button type="button" onClick={() => setDrafts(Object.fromEntries(data.fields.map((f) => [f.key, f.value])))} className="admin-btn-outline" data-testid="content-discard-all">Discard all edits</button>}
          <button type="button" onClick={save} disabled={!dirtyKeys.length || saving} className="admin-btn" data-testid="content-save-button">{saving ? "Saving…" : dirtyKeys.length ? `Save ${dirtyKeys.length} change${dirtyKeys.length === 1 ? "" : "s"}` : "No unsaved changes"}</button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2" role="tablist" aria-label="Website pages">
        {data.pages.map((p) => (
          <button key={p.id} type="button" role="tab" aria-selected={page === p.id} onClick={() => setPage(p.id)} className={`rounded-full border px-4 py-2 text-sm font-bold ${page === p.id ? "border-brand bg-brand text-white" : "border-line bg-white text-ink hover:border-brand"}`} data-testid={`content-page-${p.id}`}>
            {p.label}{dirtyIn(p.id) && <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-[#F79009]" aria-label="has unsaved edits" />}
          </button>
        ))}
      </div>

      <div className="mt-4">
        <a href={PATHS[page]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-bold text-brand underline" data-testid="content-view-page">View this page on the website<ExternalLink className="h-3.5 w-3.5" aria-hidden="true" /></a>
      </div>

      <div className="mt-6 space-y-6">
        {sections.map((s) => (
          <section key={s} className="border border-line bg-white px-5 md:px-8" aria-label={sectionLabel(s)} data-testid={`content-section-${page}-${s}`}>
            <h2 className="border-b border-line py-4 text-sm font-extrabold uppercase tracking-[0.12em] text-brand">{sectionLabel(s)}</h2>
            {fields.filter((f) => f.section === s).map((f) => <Field key={f.key} f={f} draft={drafts[f.key]} setDraft={setDraft(f.key)} error={errors[f.key]} />)}
          </section>
        ))}
      </div>
    </div>
  );
}
